// Donut / ring chart with an optional centred label. Used for composition
// breakdowns (revenue attribution, decision-ladder split, feed-status mix).

import { memo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

import { ANIMATION_ACTIVE, TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE } from "@/components/charts/theme";

export interface DonutDatum {
  name: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  data: DonutDatum[];
  height?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: string;
  tooltipFormatter?: (value: unknown, name: string) => [string, string] | string;
}

function DonutChartBase({
  data,
  height = 200,
  thickness = 18,
  centerLabel,
  centerValue,
  tooltipFormatter,
}: DonutChartProps) {
  const outer = height / 2 - 6;
  const inner = outer - thickness;

  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" debounce={60}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={inner}
            outerRadius={outer}
            paddingAngle={2}
            stroke="none"
            isAnimationActive={ANIMATION_ACTIVE}
          >
            {data.map((d) => (
              <Cell key={d.name} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            formatter={tooltipFormatter as never}
          />
        </PieChart>
      </ResponsiveContainer>
      {(centerValue || centerLabel) && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          {centerValue && <div className="text-2xl font-semibold text-zinc-100">{centerValue}</div>}
          {centerLabel && <div className="text-[11px] uppercase tracking-wider text-zinc-500">{centerLabel}</div>}
        </div>
      )}
    </div>
  );
}

export const DonutChart = memo(DonutChartBase);
